// ── Shared track rendering utilities ──
// Used by both TrackViewerPage (full interactive viewer) and TrackCanvas (static mini view)

export const TRACK_COLORS = {
  background: '#0D1B2A',
  trackShadow: 'rgba(0, 0, 0, 0.5)',
  trackBase: '#4A6A7E',
  segmentOverlay: '#2ECC71',
  markerTick: '#ECF0F1',
  labelBg: 'rgba(13, 27, 42, 0.8)',
  labelText: '#FFFFFF',
  // Zoom-box drag highlight on track map
  zoomHighlight: '#FFD600',
  zoomHighlightWidth: 5,
  zoomHighlightAlpha: 0.9,
  // Edit mode colors
  editPointDot: '#556677',
  editStartMarker: '#2ECC71',
  editStopMarker: '#E74C3C',
  editSelected: '#FFD600',
  editHover: 'rgba(255,255,255,0.6)',
  editPendingSegment: '#FFD600',
};

export const TRACK_DEFAULTS = {
  padding: 100,
  shadowWidth: 10,
  lineWidth: 4,
  segmentWidthMult: 2,
  tickHalfLen: 14,
  checkerSize: 28,
  minFontSize: 10,
  labelOffset: 20,
};

/**
 * Parse coordinates array into segments (START/STOP pairs).
 */
export function parseSegments(coordinates) {
  const segments = [];
  let currentSeg = null;
  coordinates.forEach((p, i) => {
    if (p.command === 'start') {
      currentSeg = { startIdx: i, label: p.label || '' };
    } else if (p.command === 'stop' && currentSeg) {
      currentSeg.endIdx = i;
      segments.push(currentSeg);
      currentSeg = null;
    }
  });
  return segments;
}

/**
 * Compute screen points from GPS coordinates, fitting to canvas with optional transform.
 */
export function computeScreenPoints(coordinates, canvasW, canvasH, padding, transform, mirrorX = false, bounds = null, mirrorY = false) {
  if (coordinates.length === 0) return [];

  const tf = transform || { x: 0, y: 0, scale: 1, rotation: 0 };

  let minLat, maxLat, minLng, maxLng;
  if (bounds) {
    ({ minLat, maxLat, minLng, maxLng } = bounds);
  } else {
    minLat = Infinity; maxLat = -Infinity; minLng = Infinity; maxLng = -Infinity;
    for (const p of coordinates) {
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }
  }

  const latRange = maxLat - minLat || 0.001;
  const lngRange = maxLng - minLng || 0.001;
  const vPad = Math.max(padding, canvasH * 0.08);
  const availW = canvasW - padding * 2;
  const availH = canvasH - vPad * 2;
  const scale = Math.min(availW / lngRange, availH / latRange);
  const cx = canvasW / 2;
  const cy = canvasH / 2;
  const midLat = (minLat + maxLat) / 2;
  const midLng = (minLng + maxLng) / 2;

  return coordinates.map((p) => {
    let x = -(p.lng - midLng) * scale;
    let y = (p.lat - midLat) * scale;
    if (mirrorX) x = -x;
    if (mirrorY) y = -y;

    if (tf.rotation !== 0) {
      const cos = Math.cos(tf.rotation);
      const sin = Math.sin(tf.rotation);
      const rx = x * cos - y * sin;
      const ry = x * sin + y * cos;
      x = rx;
      y = ry;
    }

    x = x * tf.scale + cx + tf.x;
    y = y * tf.scale + cy + tf.y;

    return { x, y };
  });
}

/**
 * Draw the full track onto a canvas 2D context.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array} screenPts - screen-space points from computeScreenPoints
 * @param {Array} segments - from parseSegments
 * @param {number} zoom - current zoom level (transform.scale)
 * @param {object} opts - override defaults (padding, lineWidth, etc.)
 */
export function drawTrack(ctx, screenPts, segments, zoom, opts = {}, heatmapColors = null) {
  const o = { ...TRACK_DEFAULTS, ...opts };
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;

  // Clear
  ctx.fillStyle = TRACK_COLORS.background;
  ctx.fillRect(0, 0, w, h);

  if (screenPts.length < 2) return;

  // Helper: draw polyline
  const drawPath = (pts, color, lineWidth) => {
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i].x, pts[i].y);
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  // Layer 1: Track shadow
  drawPath(screenPts, TRACK_COLORS.trackShadow, o.shadowWidth * zoom);

  // Layer 2: Track base line (or heatmap-colored segments)
  if (heatmapColors?.length) {
    for (let i = 0; i < screenPts.length - 1; i++) {
      ctx.beginPath();
      ctx.moveTo(screenPts[i].x, screenPts[i].y);
      ctx.lineTo(screenPts[i + 1].x, screenPts[i + 1].y);
      ctx.strokeStyle = heatmapColors[i] || TRACK_COLORS.trackBase;
      ctx.lineWidth = o.lineWidth * zoom * 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  } else {
    drawPath(screenPts, TRACK_COLORS.trackBase, o.lineWidth * zoom);
  }

  // Layer 3: Segment overlays
  for (const seg of segments) {
    ctx.beginPath();
    ctx.moveTo(screenPts[seg.startIdx].x, screenPts[seg.startIdx].y);
    for (let i = seg.startIdx + 1; i <= seg.endIdx && i < screenPts.length; i++) {
      ctx.lineTo(screenPts[i].x, screenPts[i].y);
    }
    ctx.strokeStyle = TRACK_COLORS.segmentOverlay;
    ctx.lineWidth = o.lineWidth * zoom * o.segmentWidthMult;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }

  // Layer 4: Marker ticks
  const drawTick = (idx) => {
    if (idx <= 0 || idx >= screenPts.length - 1) return;
    const prev = screenPts[idx - 1];
    const next = screenPts[Math.min(idx + 1, screenPts.length - 1)];
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const halfLen = o.tickHalfLen * zoom;
    ctx.beginPath();
    ctx.moveTo(screenPts[idx].x - nx * halfLen, screenPts[idx].y - ny * halfLen);
    ctx.lineTo(screenPts[idx].x + nx * halfLen, screenPts[idx].y + ny * halfLen);
    ctx.strokeStyle = TRACK_COLORS.markerTick;
    ctx.lineWidth = 2 * zoom;
    ctx.stroke();
  };

  for (const seg of segments) {
    drawTick(seg.startIdx);
    drawTick(seg.endIdx);
  }

  // Layer 5: Segment labels
  for (const seg of segments) {
    if (!seg.label) continue;
    const midIdx = Math.floor((seg.startIdx + seg.endIdx) / 2);
    if (midIdx <= 0 || midIdx >= screenPts.length - 1) continue;

    const prev = screenPts[midIdx - 1];
    const next = screenPts[Math.min(midIdx + 1, screenPts.length - 1)];
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const offset = o.labelOffset * zoom;
    const lx = screenPts[midIdx].x + nx * offset;
    const ly = screenPts[midIdx].y + ny * offset;

    const fontSize = Math.max(12 * zoom, o.minFontSize);
    ctx.font = `${fontSize}px "JetBrains Mono", "Fira Code", monospace`;
    const textW = ctx.measureText(seg.label).width;
    const padX = 6;
    const padY = 4;

    ctx.fillStyle = TRACK_COLORS.labelBg;
    ctx.beginPath();
    ctx.roundRect(lx - textW / 2 - padX, ly - fontSize / 2 - padY, textW + padX * 2, fontSize + padY * 2, 4);
    ctx.fill();

    ctx.fillStyle = TRACK_COLORS.labelText;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(seg.label, lx, ly);
  }

  // Layer 6: Start/finish checkerboard
  const sfSize = o.checkerSize * zoom;
  const cells = 5;
  const cellSize = sfSize / cells;
  const sfX = screenPts[0].x - sfSize / 2;
  const sfY = screenPts[0].y - sfSize / 2;

  for (let r = 0; r < cells; r++) {
    for (let c = 0; c < cells; c++) {
      ctx.fillStyle = (r + c) % 2 === 0 ? '#FFFFFF' : '#000000';
      ctx.fillRect(sfX + c * cellSize, sfY + r * cellSize, cellSize, cellSize);
    }
  }

  // Dashed line through start/finish
  if (screenPts.length > 1) {
    const dx = screenPts[1].x - screenPts[0].x;
    const dy = screenPts[1].y - screenPts[0].y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const halfLen = sfSize;
    ctx.beginPath();
    ctx.setLineDash([4 * zoom, 4 * zoom]);
    ctx.moveTo(screenPts[0].x - nx * halfLen, screenPts[0].y - ny * halfLen);
    ctx.lineTo(screenPts[0].x + nx * halfLen, screenPts[0].y + ny * halfLen);
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2 * zoom;
    ctx.stroke();
    ctx.setLineDash([]);
  }
}

/**
 * Find the nearest screen point to (mouseX, mouseY) within maxDistance pixels.
 * Returns the index, or -1 if none found.
 */
export function findNearestPoint(screenPts, mouseX, mouseY, maxDistance = 15) {
  let bestIdx = -1;
  let bestDist = maxDistance * maxDistance;
  for (let i = 0; i < screenPts.length; i++) {
    const dx = screenPts[i].x - mouseX;
    const dy = screenPts[i].y - mouseY;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestDist) {
      bestDist = d2;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Draw edit-mode overlay on top of the base track.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array} screenPts
 * @param {Array} coordinates - with command/label fields
 * @param {object} opts - { hoveredIdx, selectedIdx, pendingStartIdx, zoom }
 */
export function drawEditOverlay(ctx, screenPts, coordinates, opts = {}) {
  const { hoveredIdx = -1, selectedIdx = -1, pendingStartIdx = -1, zoom = 1 } = opts;
  if (screenPts.length < 2) return;

  // Adaptive point density: show fewer dots when zoomed out
  let step = 1;
  if (screenPts.length > 200) {
    // Estimate avg pixel distance between consecutive points
    let totalDist = 0;
    const sampleCount = Math.min(100, screenPts.length - 1);
    const sampleStep = Math.floor(screenPts.length / sampleCount);
    for (let i = 0; i < sampleCount; i++) {
      const idx = i * sampleStep;
      const next = Math.min(idx + sampleStep, screenPts.length - 1);
      const dx = screenPts[next].x - screenPts[idx].x;
      const dy = screenPts[next].y - screenPts[idx].y;
      totalDist += Math.sqrt(dx * dx + dy * dy);
    }
    const avgDist = totalDist / sampleCount;
    if (avgDist < 2) step = 8;
    else if (avgDist < 4) step = 4;
    else if (avgDist < 6) step = 2;
  }

  // Layer: Point dots
  const dotRadius = Math.max(2, 2.5 * zoom);
  ctx.fillStyle = TRACK_COLORS.editPointDot;
  for (let i = 0; i < screenPts.length; i += step) {
    ctx.beginPath();
    ctx.arc(screenPts[i].x, screenPts[i].y, dotRadius, 0, Math.PI * 2);
    ctx.fill();
  }

  // Layer: Segment boundary markers (always drawn, regardless of step)
  const markerRadius = Math.max(5, 6 * zoom);
  for (let i = 0; i < coordinates.length; i++) {
    const cmd = coordinates[i].command;
    if (cmd === 'start' || cmd === 'stop') {
      ctx.beginPath();
      ctx.arc(screenPts[i].x, screenPts[i].y, markerRadius, 0, Math.PI * 2);
      ctx.fillStyle = cmd === 'start' ? TRACK_COLORS.editStartMarker : TRACK_COLORS.editStopMarker;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  // Layer: Pending segment (dashed yellow from pendingStart to hovered)
  if (pendingStartIdx >= 0 && hoveredIdx >= 0 && pendingStartIdx !== hoveredIdx) {
    const fromIdx = Math.min(pendingStartIdx, hoveredIdx);
    const toIdx = Math.max(pendingStartIdx, hoveredIdx);
    ctx.beginPath();
    ctx.moveTo(screenPts[fromIdx].x, screenPts[fromIdx].y);
    for (let i = fromIdx + 1; i <= toIdx && i < screenPts.length; i++) {
      ctx.lineTo(screenPts[i].x, screenPts[i].y);
    }
    ctx.setLineDash([6 * zoom, 4 * zoom]);
    ctx.strokeStyle = TRACK_COLORS.editPendingSegment;
    ctx.lineWidth = 3 * zoom;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Layer: Hovered point
  if (hoveredIdx >= 0 && hoveredIdx < screenPts.length) {
    const r = Math.max(7, 8 * zoom);
    ctx.beginPath();
    ctx.arc(screenPts[hoveredIdx].x, screenPts[hoveredIdx].y, r, 0, Math.PI * 2);
    ctx.fillStyle = TRACK_COLORS.editHover;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  // Layer: Selected point (bright yellow + crosshair)
  if (selectedIdx >= 0 && selectedIdx < screenPts.length) {
    const sx = screenPts[selectedIdx].x;
    const sy = screenPts[selectedIdx].y;
    const r = Math.max(8, 9 * zoom);

    ctx.beginPath();
    ctx.arc(sx, sy, r, 0, Math.PI * 2);
    ctx.fillStyle = TRACK_COLORS.editSelected;
    ctx.globalAlpha = 0.5;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = TRACK_COLORS.editSelected;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Crosshair
    const ch = r + 4;
    ctx.beginPath();
    ctx.moveTo(sx - ch, sy);
    ctx.lineTo(sx + ch, sy);
    ctx.moveTo(sx, sy - ch);
    ctx.lineTo(sx, sy + ch);
    ctx.strokeStyle = TRACK_COLORS.editSelected;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}
